<template>
    <div class="w-full space-y-3">
        <Head title="Reports"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Expenses Report</h1>
            <a :href="route('frontend.reports.finance_expenses_' + (this.imported ? 'import_' : '') + 'report_export', {
                _query: {
                    search: form.search,
                    starts_at: form.filter.starts_at,
                    ends_at: form.filter.ends_at,
                    user: form.filter.user,
                    card_type: form.filter.card_type,
                    fixture: form.filter.fixture,
                    client: form.filter.client,
                    status: form.filter.status,
                    trading_entity: form.filter.trading_entity
                }})" class="py-3 px-3 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral w-40 text-center">Download</a>
        </div>
    </div>
    <br />
    <div class="grid grid-cols-4 gap-2">
        <div class="relative" v-if="trading_details.length > 1">
            <label for="trading-entity" class="block text-sm font-medium text-gray-700">Trading Entity</label>
            <select id="trading-entity" v-model="form.filter.trading_entity"
                class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                <option value="" v-html="'All'"></option>
                <option v-for="(trading_detail, index) in trading_details" :key="index" :value="trading_detail.id" :selected="form.filter.trading_entity === trading_detail.id" v-html="trading_detail.trading_name"></option>
            </select>
        </div>
        <div class="relative">
            <label for="start-date" class="block text-sm font-medium text-gray-700">Start Date <span v-if="formattedStartDate">({{ formattedStartDate }})</span></label>
            <input v-model="form.filter.starts_at" name="start-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select start date">
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700">End Date <span v-if="formattedEndDate">({{ formattedEndDate }})</span></label>
            <input v-model="form.filter.ends_at" name="end-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select end date">
        </div>
        <search-filter :show-filter="false" v-model:value="form.search" @reset="reset" class="mt-7"></search-filter>
    </div>
    <div class="grid grid-cols-5 gap-2 mt-2">
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700 invisible">Employee Name</label>
            <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="users" label="name" :reduce="users => users.id" v-model="form.filter.user" placeholder="Employee Name" />
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700 invisible">Card Type</label>
            <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="card_types" label="name" v-model="form.filter.card_type" placeholder="Card Type" />
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700 invisible">Fixture</label>
            <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="fixtures" label="title" :reduce="fixtures => fixtures.id" v-model="form.filter.fixture" placeholder="Fixture" />
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700 invisible">Client</label>
            <v-select :disabled="form.fixture == 0" class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="clients" label="name" :reduce="clients => clients.id" v-model="form.filter.client" placeholder="Client" />
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700 invisible">Status</label>
            <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="statuses" label="name" v-model="form.filter.status" placeholder="Status" />
        </div>
    </div>
    <br />
    <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Employee Name
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Card Number
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Payment Date
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Net Amount
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        VAT Amount
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Expense Amount
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        GBP Amount
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Expense Type
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Supplier
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Client
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Reference
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Type of Transaction
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Nominal
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Cost Centre
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Attachments
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="Object.values(expenses).length > 0" v-for="(expense, index) in expenses.data" :key="index">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.user?.name ?? 'N/A' }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            <span v-html="expense.card ? expense.card.card_number.substring(expense.card.card_number.length - 4) + ' <i>(Last 4)</i>' : 'Card Not Available'"></span>
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ moment(expense.paid_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ formatCurrency((expense.amount - expense.vat_amount), expense.currency.code ?? 'GBP' , true) }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ formatCurrency(expense.vat_amount, expense.currency.code ?? 'GBP' , true) }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ formatCurrency(expense.amount, expense.currency.code ?? 'GBP' , true) }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ formatCurrency(expense.gbp_amount, 'GBP' , true) }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ expense.expense_type.name }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ expense.supplier }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ decodeEntities(expense.description?.replace(/<\/?[^>]+>/ig, " ")) }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer" @click="goToExpenseDetails(expense)">
                                        <a v-if="expense.fixture">
                                            {{ expense.fixture.client.name }}
                                        </a>
                                        <span v-else>N/A</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer" @click="goToExpenseDetails(expense)">
                                        <a v-if="expense.fixture">
                                            {{ expense.fixture.title }}
                                        </a>
                                        <span v-else>N/A</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer" @click="goToExpenseDetails(expense)">
                                        <a v-if="expense.fixture">
                                            {{ expense.fixture.reference }}
                                        </a>
                                        <span v-else>N/A</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ expense.transaction_type.name }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ expense.status }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ expense.transaction_type.nominal }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a>
                                            {{ expense.transaction_type.cost_center }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex">
                                        <div class="ml-4 flex-shrink-0" v-for="(receipt,index) in expense.receipts">
                                            <a :href="route('frontend.expenses.expense.download', [expense.id, receipt])" class="font-medium text-pitch-purple hover:text-light-pitch-purple"> <DownloadIcon class="w-4 h-4" /> </a>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-else>
                                    <td colspan="13" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        No expenses available.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="expenses.links" />
        </div>
    </div>
</template>

<script>
    import moment from "moment";
    import pickBy from "lodash/pickBy";
    import debounce from "lodash/debounce";
    import mapValues from "lodash/mapValues";
    import Pagination from "@/Components/Pagination";
    import {DownloadIcon} from '@heroicons/vue/solid';
    import {Link, Head, useForm} from "@inertiajs/vue3";
    import Authenticated from "@/Layouts/Authenticated";
    import SearchFilter from "@/Components/SearchFilter";

    export default {
        layout: Authenticated,
        components: {
            Head,
            Link,
            Pagination,
            DownloadIcon,
            SearchFilter
        },
        props:['users', 'fixtures', 'clients', 'expenses', 'filters', 'selected_data', 'imported', 'trading_details'],
        created() {
            this.moment = moment
        },
        data(props) {
            return {
                form: useForm({
                    search: this.filters.search,
                    filter: {
                        starts_at: this.filters.starts_at,
                        ends_at: this.filters.ends_at,
                        user: this.selected_data.user,
                        card_type: this.filters.card_type,
                        fixture: this.selected_data.fixture,
                        client: this.selected_data.client,
                        status: this.filters.status,
                        trading_entity: null
                    }
                }),
                card_types: [
                    { name: 'Company Credit Card', value: 'Company Credit Card', label: 'Company Credit Card' },
                    { name: 'Personal Payment', value: 'Personal Payment', label: 'Personal Payment' },
                ],
                statuses: [
                    { name: 'Confirmed', value: 'Confirmed', label: 'Confirmed' },
                    { name: 'Estimated', value: 'Estimated', label: 'Estimated' },
                ],
            };
        },
        computed: {
            formattedStartDate() {
                if (this.form.filter.starts_at) {
                    return moment(this.form.filter.starts_at).format('DD MMM YYYY');
                }
                return '';
            },
            formattedEndDate() {
                if (this.form.filter.ends_at) {
                    return moment(this.form.filter.ends_at).format('DD MMM YYYY');
                }
                return '';
            }
        },
        methods: {
            reset() {
                this.form.search = null;
            },
            goToExpenseDetails(expense) {
                if(expense.fixture_id !== 0) {
                    // Redirect to non fixture expense
                    window.location.href = `/fixtures/`+expense.fixture_id+'/expenses';
                } else {
                    // Redirect to non fixture expense
                    window.location.href = `/expenses`;
                }
            },
            decodeEntities(encodedString) {
                const parser = new DOMParser();
                const dom = parser.parseFromString(`<!doctype html><body>${encodedString}`, 'text/html');
                return dom.body.textContent;
            }
        },
        watch: {
            'form.fixture': function(newVal) {
                if (newVal == 0) {
                    this.form.client = '';
                }
            },
            form: {
                deep: true,
                handler: debounce(function () {
                    this.$inertia.get(route('frontend.reports.finance_expenses_' + (this.imported ? 'import_' : '') + 'report'), pickBy(this.form), {preserveState: true, preserveScroll: true})
                }, 500),
            },
        }
    }
</script>
